import { FormEvent, useEffect, useState } from 'react';
import { PageShell } from '../../components/DataTablePanel';
import {
  Button,
  Callout,
  Label,
  LoadingSpinner,
  SaveIcon,
  Switch,
  TextInput,
} from '../../components/ui';
import { t } from '../../i18n';
import { ApiError, api, type PlatformSettings } from '../../services/api';

export function PlatformSettingsPage() {
  const labels = t().platformSettings;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpSecure, setSmtpSecure] = useState(false);
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPass, setSmtpPass] = useState('');
  const [mailFrom, setMailFrom] = useState('');
  const [passConfigured, setPassConfigured] = useState(false);
  const [mailConfigured, setMailConfigured] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const settings = await api.getPlatformSettings();
      applySettings(settings);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setLoading(false);
    }
  }

  function applySettings(settings: PlatformSettings) {
    setSmtpHost(settings.smtpHost);
    setSmtpPort(String(settings.smtpPort || 587));
    setSmtpSecure(settings.smtpSecure);
    setSmtpUser(settings.smtpUser);
    setSmtpPass('');
    setMailFrom(settings.mailFrom);
    setPassConfigured(settings.smtpPassConfigured);
    setMailConfigured(settings.mailConfigured);
  }

  useEffect(() => {
    void load();
  }, []);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setMessage(null);

    const port = Number(smtpPort);
    if (!Number.isInteger(port) || port < 1 || port > 65535) {
      setError(labels.invalidPort);
      return;
    }

    setSaving(true);
    try {
      const updated = await api.updatePlatformSettings({
        smtpHost: smtpHost.trim() || null,
        smtpPort: port,
        smtpSecure,
        smtpUser: smtpUser.trim() || null,
        ...(smtpPass.trim() ? { smtpPass: smtpPass.trim() } : {}),
        mailFrom: mailFrom.trim() || null,
      });
      applySettings(updated);
      setMessage(labels.saved);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <PageShell>
        <div className="flex flex-1 items-center justify-center py-16">
          <LoadingSpinner />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="max-w-2xl">
        <p className="mb-4 text-sm text-muted">{labels.hint}</p>

        {message ? <Callout variant="success">{message}</Callout> : null}
        {error ? <Callout variant="danger">{error}</Callout> : null}

        <form
          onSubmit={handleSubmit}
          className="mt-4 space-y-5 rounded-xl border border-line bg-panel p-5 shadow-bf-sm"
        >
          <div>
            <h2 className="font-display text-base font-semibold text-ink">
              {labels.smtpSection}
            </h2>
            <p className="mt-1 text-xs text-muted">{labels.smtpHint}</p>
            {mailConfigured ? (
              <p className="mt-2 text-xs font-medium text-teal-700 dark:text-teal-300">
                {labels.mailReady}
              </p>
            ) : (
              <p className="mt-2 text-xs font-medium text-amber-700 dark:text-amber-300">
                {labels.mailNotReady}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="smtp-host">{labels.smtpHost}</Label>
            <TextInput
              id="smtp-host"
              value={smtpHost}
              onChange={(e) => setSmtpHost(e.target.value)}
              placeholder="smtp.example.com"
              autoComplete="off"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="smtp-port">{labels.smtpPort}</Label>
              <TextInput
                id="smtp-port"
                value={smtpPort}
                onChange={(e) => setSmtpPort(e.target.value)}
                inputMode="numeric"
                autoComplete="off"
              />
            </div>
            <div className="flex items-end">
              <div className="flex w-full items-center justify-between gap-3 rounded-lg border border-line px-3 py-2.5">
                <span className="text-sm">{labels.smtpSecure}</span>
                <Switch
                  checked={smtpSecure}
                  onChange={(e) => setSmtpSecure(e.target.checked)}
                />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="smtp-user">{labels.smtpUser}</Label>
            <TextInput
              id="smtp-user"
              value={smtpUser}
              onChange={(e) => setSmtpUser(e.target.value)}
              autoComplete="off"
            />
          </div>

          <div>
            <Label htmlFor="smtp-pass">{labels.smtpPass}</Label>
            <TextInput
              id="smtp-pass"
              type="password"
              value={smtpPass}
              onChange={(e) => setSmtpPass(e.target.value)}
              autoComplete="new-password"
              placeholder={
                passConfigured ? labels.smtpPassKeep : labels.smtpPassPlaceholder
              }
            />
            <p className="mt-1 text-xs text-muted">
              {passConfigured ? labels.smtpPassHintSet : labels.smtpPassHintEmpty}
            </p>
          </div>

          <div>
            <Label htmlFor="mail-from">{labels.mailFrom}</Label>
            <TextInput
              id="mail-from"
              value={mailFrom}
              onChange={(e) => setMailFrom(e.target.value)}
              placeholder='BalanceFlow <noreply@example.com>'
              autoComplete="off"
            />
          </div>

          <div className="flex justify-end pt-2">
            <Button type="submit" loading={saving}>
              <SaveIcon className="h-4 w-4" />
              {t().common.save}
            </Button>
          </div>
        </form>
      </div>
    </PageShell>
  );
}
